import { useCallback, useEffect, useRef, useState } from 'react';

import { WORKSPACE_AGENT_HOVER_OPEN_DELAY_MS } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentHoverDelays';

type WorkspaceAgentMentionRowPreviewArm = {
  query: string;
  scopeKey: string;
};

export type UseWorkspaceAgentMentionRowPreviewParams = {
  /**
   * The highlighted row, when it is one a preview can be drawn for. `undefined` — a row with no still,
   * a board row, or no rows at all — closes the preview: there is nothing left for it to show.
   */
  itemId: string | undefined;
  /**
   * Mention source and, on Contexts, the selected kit. Must match the arm's `scopeKey` or the preview
   * is treated as disarmed — including mid-render when the kit changes, so a pending open timer cannot
   * fire for the previous list.
   */
  previewResetScopeKey: string;
  /** A new query is a new set of rows, none of which the user has pointed at yet. */
  query: string;
};

export type UseWorkspaceAgentMentionRowPreviewResult = {
  /** The user put the highlight on a row themselves — a hover, or an arrow key. */
  armRowPreview: () => void;
  closeRowPreview: () => void;
  /**
   * True only after that move, not because the list highlighted its first row. The still fetch starts
   * here so it can land during the open delay, rather than against a picker nobody has pointed at.
   */
  isRowPreviewArmed: boolean;
  isRowPreviewOpen: boolean;
};

/**
 * Whether the highlighted picker row is showing its preview. Arming is deliberate rather than derived
 * from the highlight: the list highlights its first row on every render, and a preview driven by that
 * alone would appear over a picker the user has not touched — and would jump to whatever row a
 * refetch put on top. Typing forgets the arm, including when the previous query comes back: the list
 * has already highlighted its first row, which nobody pointed at.
 */
export const useWorkspaceAgentMentionRowPreview = ({
  itemId,
  previewResetScopeKey,
  query,
}: UseWorkspaceAgentMentionRowPreviewParams): UseWorkspaceAgentMentionRowPreviewResult => {
  const [armedScope, setArmedScope] = useState<WorkspaceAgentMentionRowPreviewArm>();
  const [isRowPreviewOpen, setIsRowPreviewOpen] = useState(false);
  // Read by `armRowPreview`, which is handed to a memoised key handler and must not rebind per keystroke.
  const queryRef = useRef(query);
  const previewResetScopeKeyRef = useRef(previewResetScopeKey);
  // Read by the open effect below without re-running it: whether a preview is already up decides the wait,
  // and a dependency on the flag that effect sets would restart the wait the moment it opened.
  const isRowPreviewOpenRef = useRef<boolean>(isRowPreviewOpen);

  const isArmed = armedScope?.query === query && armedScope.scopeKey === previewResetScopeKey;

  const closePreview = useCallback(() => {
    isRowPreviewOpenRef.current = false;
    setIsRowPreviewOpen(false);
  }, []);

  /**
   * Both mirrors are written after the commit rather than during the render that produced them: a render
   * can be thrown away or run twice, and only what was committed is what the handler and the effect below
   * are reading. Declared first so a commit that both opens the preview and moves the row has the flag
   * already true by the time the open effect looks at it.
   */
  useEffect(() => {
    isRowPreviewOpenRef.current = isRowPreviewOpen;
    queryRef.current = query;
    previewResetScopeKeyRef.current = previewResetScopeKey;
  }, [isRowPreviewOpen, previewResetScopeKey, query]);

  useEffect(() => {
    setArmedScope((current) =>
      current?.query === query && current?.scopeKey === previewResetScopeKey ? current : undefined,
    );
  }, [previewResetScopeKey, query]);

  useEffect(() => {
    if (!isArmed || !itemId) {
      closePreview();

      return;
    }

    // Already reading previews: the next row swaps straight away rather than blinking out and back.
    if (isRowPreviewOpenRef.current) {
      return;
    }

    const timeout = window.setTimeout(() => {
      isRowPreviewOpenRef.current = true;
      setIsRowPreviewOpen(true);
    }, WORKSPACE_AGENT_HOVER_OPEN_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [closePreview, isArmed, itemId]);

  const armRowPreview = useCallback(() => {
    setArmedScope({ query: queryRef.current, scopeKey: previewResetScopeKeyRef.current });
  }, []);

  const closeRowPreview = useCallback(() => {
    setArmedScope(undefined);
    closePreview();
  }, [closePreview]);

  return { armRowPreview, closeRowPreview, isRowPreviewArmed: isArmed, isRowPreviewOpen };
};
